import { ReactElement, ReactNode, useEffect, useState } from "react";
import { Outlet, RouteObject, RouterProvider, useBlocker } from "react-router";

import * as Sentry from "@sentry/react";
import { useAuth0 } from "@auth0/auth0-react";
import FourOhFour from "src/pages/FourOhFour";

import Loading from "@components/Utility/Loading";
import RouteChunkErrorFallback from "@features/appVersion/RouteChunkErrorFallback";
import {
  attemptStaleReload,
  isChunkLoadError,
} from "@features/appVersion/deployVersionGuard";
import { isAppStale, markAppStale } from "@features/appVersion/staleAppState";
import { UserRole } from "@models/UserRole";
import { sentryCreateBrowserRouterV7 } from "@services/sentry";
import { TScope, roleHasPermission } from "@utils/permissions";
import useRoleIs from "@utils/useRoleIs";

import { protectedRoute } from "./types";

// Once a newer deploy is detected, turn the next navigation into a full
// document load so the user picks up fresh assets at a moment where nothing
// in progress is lost. A blocker is required (rather than router.subscribe):
// no routes have loaders, so SPA navigations complete synchronously and are
// only observable after the fact.
const StaleNavigationGuard = () => {
  const blocker = useBlocker(() => isAppStale());

  useEffect(() => {
    if (blocker.state === "blocked") {
      const { pathname, search, hash } = blocker.location;
      window.location.assign(pathname + search + hash);
    }
  }, [blocker]);

  return <Outlet />;
};

interface ProtectedRouteProps {
  element?: ReactNode;
  allowedRoles?: readonly (UserRole | string)[];
  permissions?: TScope | TScope[];
}

const ProtectedRoute = ({
  element,
  allowedRoles,
  permissions,
}: ProtectedRouteProps): ReactElement => {
  const roleIs = useRoleIs();

  const allowed = roleHasPermission(
    roleIs.role,
    permissions,
    // fallback to role-based permissions if no scope is defined
    !allowedRoles || allowedRoles.includes(roleIs.role)
  );

  if (!allowed) return <FourOhFour />;

  return element ? <>{element}</> : <Outlet />;
};

// Recursively filters protectedRoutes based on the user's role.
// If the user is not allowed to access a route, return a 404 page without
// children, otherwise recursively protect child routes.
const protectRoutes = (routes: protectedRoute[]): RouteObject[] =>
  routes.map(
    ({
      allowedRoles,
      permissions,
      defaultChildrenAllowedRoles,
      children,
      element,
      index,
      ...route
    }) => {
      const protectedElement = (
        <ProtectedRoute
          element={element}
          allowedRoles={allowedRoles}
          permissions={permissions}
        />
      );

      if (index) {
        return { ...route, index, element: protectedElement };
      }

      return {
        ...route,
        element: protectedElement,
        children:
          children &&
          protectRoutes(
            children.map((child) => ({
              ...child,
              allowedRoles: child.allowedRoles ?? defaultChildrenAllowedRoles,
            }))
          ),
      };
    }
  );

const createProtectedRouter = (routes: protectedRoute[]) =>
  sentryCreateBrowserRouterV7([
    { element: <StaleNavigationGuard />, children: protectRoutes(routes) },
  ]);

const ProtectedRouterProvider = () => {
  const { isLoading: isAuthLoading } = useAuth0();
  const [routes, setRoutes] = useState<protectedRoute[] | null>(null);
  const [router, setRouter] = useState<ReturnType<
    typeof sentryCreateBrowserRouterV7
  > | null>(null);
  const [hasBootError, setHasBootError] = useState(false);

  // Load route definitions while Auth0 initializes so neither blocks the other.
  useEffect(() => {
    let cancelled = false;

    import("./routesRoot")
      .then((module) => {
        if (!cancelled) {
          setRoutes(module.default);
        }
      })
      .catch((error: unknown) => {
        const chunkError = isChunkLoadError(error);

        if (chunkError && attemptStaleReload()) {
          return;
        }

        if (chunkError) {
          markAppStale("chunk-load-error");
        } else {
          Sentry.captureException(error);
        }

        if (!cancelled) {
          setHasBootError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // createBrowserRouter snapshots the URL at creation, so wait until Auth0 has
  // restored the post-login URL before turning the loaded routes into a router.
  useEffect(() => {
    if (isAuthLoading || !routes || router) return;

    try {
      setRouter(createProtectedRouter(routes));
    } catch (error: unknown) {
      Sentry.captureException(error);
      setHasBootError(true);
    }
  }, [isAuthLoading, router, routes]);

  if (hasBootError) {
    return <RouteChunkErrorFallback />;
  }

  if (isAuthLoading || !router) return <Loading />;

  return <RouterProvider router={router} />;
};

export default ProtectedRouterProvider;
